import { useTranslation } from 'core/i18n/i18n';
import { POCFieldAttribute } from 'modules/settings/pods/settings-integrations/service';
import { useCallback, useMemo, useState } from 'react';
import { isValidMapping } from '../service/active-directory.business';
import {
    ActiveDirectoryDetailsStep,
    ActiveDirectoryDetailsSteps,
    IdentityProviderMapping,
    TestConnectionState,
    TestConnectionStatus,
} from '../types/active-directory.type';

interface UseActiveDirectoryStepsProps {
    isEditMode: boolean;
    isDirty: boolean;
    testConnectionState: TestConnectionState;
    mappings: IdentityProviderMapping[];
    identityProviderAttributes: string[];
    pocFields: POCFieldAttribute[];
}

const areValidMappingValues = (
    mappings: IdentityProviderMapping[],
    identityProviderAttributes: string[],
    pocFields: POCFieldAttribute[]
): boolean =>
    mappings.every(
        (mapping) =>
            identityProviderAttributes.includes(mapping.identityProviderField) &&
            pocFields.map((pf) => pf.attribute).includes(mapping.operatorField)
    );

export const useActiveDirectorySteps = ({
    isEditMode,
    isDirty,
    testConnectionState,
    mappings,
    identityProviderAttributes,
    pocFields,
}: UseActiveDirectoryStepsProps) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState<ActiveDirectoryDetailsStep>(ActiveDirectoryDetailsSteps.CONNECTION);

    const handleBack = useCallback(() => {
        setCurrentStep(ActiveDirectoryDetailsSteps.CONNECTION);
    }, []);

    const handleNext = useCallback(() => {
        setCurrentStep(ActiveDirectoryDetailsSteps.DATA_POINTS);
    }, []);

    const getIsStepValid = useCallback(
        (step: ActiveDirectoryDetailsStep): boolean => {
            if (step === ActiveDirectoryDetailsSteps.CONNECTION) {
                return isDirty ? testConnectionState.status === TestConnectionStatus.SUCCESS : isEditMode;
            }

            if (step === ActiveDirectoryDetailsSteps.DATA_POINTS) {
                return (
                    mappings.length > 0 &&
                    isValidMapping(mappings) &&
                    areValidMappingValues(mappings, identityProviderAttributes, pocFields)
                );
            }

            return false;
        },
        [isEditMode, isDirty, testConnectionState, mappings, identityProviderAttributes, pocFields]
    );

    const headerTitle = useMemo(() => {
        const isCurrentStepConnection = currentStep === ActiveDirectoryDetailsSteps.CONNECTION;

        if (isEditMode) {
            return isCurrentStepConnection
                ? t('settings.integrations.active-directory.detail.header.edit.connection')
                : t('settings.integrations.active-directory.detail.header.edit.data-points');
        }

        return isCurrentStepConnection
            ? t('settings.integrations.active-directory.detail.header.add.connection')
            : t('settings.integrations.active-directory.detail.header.add.data-points');
    }, [isEditMode, currentStep, t]);

    return {
        currentStep,
        headerTitle,
        getIsStepValid,
        handleBack,
        handleNext,
    };
};
